package dev.lingvo.site.components.editor

import kotlinx.browser.document
import kotlinx.browser.window

internal enum class EditorEvidenceEventType(val wireName: String) {
    LAYOUT_MEASUREMENT_START("layout-measurement-start"),
    LAYOUT_MEASUREMENT_RESULT("layout-measurement-result"),
    LAYOUT_MEASUREMENT_INVALIDATED("layout-measurement-invalidated"),
    LAYOUT_MEASUREMENT_STALE_IGNORED("layout-measurement-stale-ignored"),
    LAYOUT_MEASUREMENT_ABANDONED("layout-measurement-abandoned"),
    TRANSITION("transition"),
    EFFECT_START("effect-start"),
    EFFECT_COMPLETE("effect-complete"),
    EFFECT_ACK("effect-ack"),
    SCROLL("scroll"),
    FOCUS("focus"),
    ANNOUNCEMENT("announcement"),
}

internal data class EditorEvidenceHookCounts(
    val workspace: Int,
    val probe: Int,
    val queue: Int,
    val queueScroll: Int,
    val filters: Int,
    val editorScroll: Int,
    val editorContent: Int,
    val source: Int,
    val target: Int,
    val pluralTabs: Int,
    val pluralPanel: Int,
    val pluralPreview: Int,
    val utility: Int,
    val compactSummary: Int,
    val utilityBody: Int,
    val details: Int,
    val commentComposer: Int,
    val actionBar: Int,
    val primary: Int,
    val conflict: Int,
    val lTabs: Int,
    val lTabsPanels: Int,
)

internal sealed interface EditorEvidenceEvent {
    val type: EditorEvidenceEventType

    data class MeasurementPhase(
        override val type: EditorEvidenceEventType,
        val layoutMeasurementId: Long,
        val disposition: String,
        val gutterPx: Double?,
        val sidePaddingPx: Int,
        val layout: String,
        val reason: String,
        val hooks: EditorEvidenceHookCounts,
        val effectStartCount: Int,
        val measurement: EditorWorkbenchMeasurement?,
    ) : EditorEvidenceEvent

    data class MeasurementLifecycle(
        override val type: EditorEvidenceEventType,
        val observedLayoutMeasurementId: Long,
        val currentLayoutMeasurementId: Long,
    ) : EditorEvidenceEvent

    data class Transition(
        override val type: EditorEvidenceEventType = EditorEvidenceEventType.TRANSITION,
        val name: String,
        val fromDisposition: String,
        val toDisposition: String,
        val fromPrimaryView: String,
        val toPrimaryView: String,
        val fromQueueMode: String,
        val toQueueMode: String,
        val fromUtility: String,
        val toUtility: String,
        val emittedEffectIds: List<Long>,
    ) : EditorEvidenceEvent

    data class Effect(
        override val type: EditorEvidenceEventType,
        val effectId: Long,
        val effectKind: String,
        val outcome: String?,
        val detail: String?,
        val layoutMeasurementId: Long,
    ) : EditorEvidenceEvent

    data class Scroll(
        override val type: EditorEvidenceEventType = EditorEvidenceEventType.SCROLL,
        val revision: Long,
        val requestedTopPx: Double,
        val actualTopPx: Double,
        val effectId: Long?,
    ) : EditorEvidenceEvent

    data class Focus(
        override val type: EditorEvidenceEventType = EditorEvidenceEventType.FOCUS,
        val requestedRef: String,
        val actualElementId: String?,
        val fallbackUsed: Boolean,
        val effectId: Long,
    ) : EditorEvidenceEvent

    data class Announcement(
        override val type: EditorEvidenceEventType = EditorEvidenceEventType.ANNOUNCEMENT,
        val message: String,
    ) : EditorEvidenceEvent
}

internal object EditorEvidenceEvents {
    /**
     * A probe was abandoned by the watchdog rather than accepted.
     *
     * Emitted so a run that hits the backstop is visible in evidence rather than looking like an
     * ordinary settle. If this ever appears in normal use, the accept path has a new hole in it.
     */
    fun layoutMeasurementAbandoned(
        layoutMeasurementId: Long,
        hadMeasurement: Boolean,
    ): EditorEvidenceEvent.MeasurementPhase = EditorEvidenceEvent.MeasurementPhase(
        type = EditorEvidenceEventType.LAYOUT_MEASUREMENT_ABANDONED,
        layoutMeasurementId = layoutMeasurementId,
        disposition = EditorMeasuredLayoutDisposition.MEASURING.name,
        gutterPx = null,
        sidePaddingPx = EDITOR_PREFERRED_SIDE_PADDING_PX,
        layout = "measuring",
        reason = if (hadMeasurement) "timeout-with-measurement" else "timeout-without-measurement",
        hooks = readEditorEvidenceHookCounts(),
        effectStartCount = 0,
        measurement = null,
    )

    fun layoutMeasurementStart(
        state: EditorWorkspaceState,
        hooks: EditorEvidenceHookCounts,
        effectStartCount: Int,
    ): EditorEvidenceEvent.MeasurementPhase = EditorEvidenceEvent.MeasurementPhase(
        type = EditorEvidenceEventType.LAYOUT_MEASUREMENT_START,
        layoutMeasurementId = state.layoutMeasurementId,
        disposition = state.measuredLayoutDisposition.name,
        gutterPx = state.measuredScrollbarGutterPx,
        sidePaddingPx = state.editorSidePaddingPx,
        layout = state.renderedLayout().name.lowercase(),
        reason = state.layoutReason(),
        hooks = hooks,
        effectStartCount = effectStartCount,
        measurement = null,
    )

    fun layoutMeasurementResult(
        state: EditorWorkspaceState,
        measurement: EditorWorkbenchMeasurement,
        hooks: EditorEvidenceHookCounts,
        effectStartCount: Int,
    ): EditorEvidenceEvent.MeasurementPhase = EditorEvidenceEvent.MeasurementPhase(
        type = EditorEvidenceEventType.LAYOUT_MEASUREMENT_RESULT,
        layoutMeasurementId = state.layoutMeasurementId,
        disposition = state.measuredLayoutDisposition.name,
        gutterPx = measurement.scrollbarGutter,
        sidePaddingPx = state.editorSidePaddingPx,
        layout = state.renderedLayout().name.lowercase(),
        reason = state.layoutReason(),
        hooks = hooks,
        effectStartCount = effectStartCount,
        measurement = measurement,
    )

    fun layoutMeasurementInvalidated(
        previousId: Long,
        nextId: Long,
    ): EditorEvidenceEvent.MeasurementLifecycle = EditorEvidenceEvent.MeasurementLifecycle(
        type = EditorEvidenceEventType.LAYOUT_MEASUREMENT_INVALIDATED,
        observedLayoutMeasurementId = previousId,
        currentLayoutMeasurementId = nextId,
    )

    fun layoutMeasurementStaleIgnored(
        staleId: Long,
        currentId: Long,
    ): EditorEvidenceEvent.MeasurementLifecycle = EditorEvidenceEvent.MeasurementLifecycle(
        type = EditorEvidenceEventType.LAYOUT_MEASUREMENT_STALE_IGNORED,
        observedLayoutMeasurementId = staleId,
        currentLayoutMeasurementId = currentId,
    )

    fun transition(
        name: String,
        before: EditorWorkspaceState,
        transition: EditorTransition,
    ): EditorEvidenceEvent.Transition = EditorEvidenceEvent.Transition(
        name = name,
        fromDisposition = before.measuredLayoutDisposition.name,
        toDisposition = transition.state.measuredLayoutDisposition.name,
        fromPrimaryView = before.primaryView.name,
        toPrimaryView = transition.state.primaryView.name,
        fromQueueMode = before.queueMode.name,
        toQueueMode = transition.state.queueMode.name,
        fromUtility = before.utility.name,
        toUtility = transition.state.utility.name,
        emittedEffectIds = transition.effects.map { it.id },
    )

    fun effectStart(
        effect: EditorViewEffect,
        layoutMeasurementId: Long,
    ): EditorEvidenceEvent.Effect = EditorEvidenceEvent.Effect(
        type = EditorEvidenceEventType.EFFECT_START,
        effectId = effect.id,
        effectKind = effect.evidenceKind(),
        outcome = null,
        detail = null,
        layoutMeasurementId = layoutMeasurementId,
    )

    fun effectComplete(
        effect: EditorViewEffect,
        result: EditorViewEffectResult,
        layoutMeasurementId: Long,
    ): EditorEvidenceEvent.Effect = effectResult(
        type = EditorEvidenceEventType.EFFECT_COMPLETE,
        effect = effect,
        result = result,
        layoutMeasurementId = layoutMeasurementId,
    )

    fun effectAck(
        effect: EditorViewEffect,
        result: EditorViewEffectResult,
        layoutMeasurementId: Long,
    ): EditorEvidenceEvent.Effect = effectResult(
        type = EditorEvidenceEventType.EFFECT_ACK,
        effect = effect,
        result = result,
        layoutMeasurementId = layoutMeasurementId,
    )

    fun scroll(
        revision: Long,
        requestedTopPx: Double,
        actualTopPx: Double,
        effectId: Long?,
    ): EditorEvidenceEvent.Scroll = EditorEvidenceEvent.Scroll(
        revision = revision,
        requestedTopPx = requestedTopPx,
        actualTopPx = actualTopPx,
        effectId = effectId,
    )

    fun focus(
        requested: EditorFocusRef,
        actualId: String?,
        fallbackUsed: Boolean,
        effectId: Long,
    ): EditorEvidenceEvent.Focus = EditorEvidenceEvent.Focus(
        requestedRef = requested.evidenceName(),
        actualElementId = actualId,
        fallbackUsed = fallbackUsed,
        effectId = effectId,
    )

    fun announcement(message: String): EditorEvidenceEvent.Announcement =
        EditorEvidenceEvent.Announcement(message = message)

    private fun effectResult(
        type: EditorEvidenceEventType,
        effect: EditorViewEffect,
        result: EditorViewEffectResult,
        layoutMeasurementId: Long,
    ): EditorEvidenceEvent.Effect = EditorEvidenceEvent.Effect(
        type = type,
        effectId = effect.id,
        effectKind = effect.evidenceKind(),
        outcome = result.outcome.name,
        detail = result.detail,
        layoutMeasurementId = layoutMeasurementId,
    )
}

internal const val EDITOR_EVIDENCE_EVENT_NAME = "lingvo:editor-evidence"

internal fun emitEditorEvidence(event: EditorEvidenceEvent) {
    val detail = js("{}")
    detail.type = event.type.wireName
    when (event) {
        is EditorEvidenceEvent.MeasurementPhase -> {
            detail.layoutMeasurementId = jsNumber(event.layoutMeasurementId)
            detail.disposition = event.disposition
            detail.gutterPx = event.gutterPx
            detail.sidePaddingPx = event.sidePaddingPx
            detail.layout = event.layout
            detail.reason = event.reason
            detail.hooks = evidenceHooksDetail(event.hooks)
            detail.effectStartCount = event.effectStartCount
            detail.measurement = event.measurement?.let(::evidenceMeasurementDetail)
        }

        is EditorEvidenceEvent.MeasurementLifecycle -> {
            detail.observedLayoutMeasurementId = jsNumber(event.observedLayoutMeasurementId)
            detail.currentLayoutMeasurementId = jsNumber(event.currentLayoutMeasurementId)
        }

        is EditorEvidenceEvent.Transition -> {
            detail.name = event.name
            detail.fromDisposition = event.fromDisposition
            detail.toDisposition = event.toDisposition
            detail.fromPrimaryView = event.fromPrimaryView
            detail.toPrimaryView = event.toPrimaryView
            detail.fromQueueMode = event.fromQueueMode
            detail.toQueueMode = event.toQueueMode
            detail.fromUtility = event.fromUtility
            detail.toUtility = event.toUtility
            detail.emittedEffectIds = event.emittedEffectIds.map(::jsNumber).toTypedArray()
        }

        is EditorEvidenceEvent.Effect -> {
            detail.effectId = jsNumber(event.effectId)
            detail.effectKind = event.effectKind
            detail.outcome = event.outcome
            detail.detail = event.detail
            detail.layoutMeasurementId = jsNumber(event.layoutMeasurementId)
        }

        is EditorEvidenceEvent.Scroll -> {
            detail.revision = jsNumber(event.revision)
            detail.requestedTopPx = event.requestedTopPx
            detail.actualTopPx = event.actualTopPx
            detail.effectId = nullableJsNumber(event.effectId)
        }

        is EditorEvidenceEvent.Focus -> {
            detail.requestedRef = event.requestedRef
            detail.actualElementId = event.actualElementId
            detail.fallbackUsed = event.fallbackUsed
            detail.effectId = jsNumber(event.effectId)
        }

        is EditorEvidenceEvent.Announcement -> detail.message = event.message
    }

    val customEvent = document.createEvent("CustomEvent")
    customEvent.asDynamic().initCustomEvent(
        EDITOR_EVIDENCE_EVENT_NAME,
        false,
        false,
        detail,
    )
    window.dispatchEvent(customEvent)
}

private fun jsNumber(value: Long): Double = value.toDouble()

private fun nullableJsNumber(value: Long?): dynamic =
    if (value == null) null else jsNumber(value)

private fun evidenceHooksDetail(hooks: EditorEvidenceHookCounts): dynamic {
    val detail = js("{}")
    detail.workspace = hooks.workspace
    detail.probe = hooks.probe
    detail.queue = hooks.queue
    detail.queueScroll = hooks.queueScroll
    detail.filters = hooks.filters
    detail.editorScroll = hooks.editorScroll
    detail.editorContent = hooks.editorContent
    detail.source = hooks.source
    detail.target = hooks.target
    detail.pluralTabs = hooks.pluralTabs
    detail.pluralPanel = hooks.pluralPanel
    detail.pluralPreview = hooks.pluralPreview
    detail.utility = hooks.utility
    detail.compactSummary = hooks.compactSummary
    detail.utilityBody = hooks.utilityBody
    detail.details = hooks.details
    detail.commentComposer = hooks.commentComposer
    detail.actionBar = hooks.actionBar
    detail.primary = hooks.primary
    detail.conflict = hooks.conflict
    detail.lTabs = hooks.lTabs
    detail.lTabsPanels = hooks.lTabsPanels
    return detail
}

private fun evidenceMeasurementDetail(measurement: EditorWorkbenchMeasurement): dynamic {
    val detail = js("{}")
    detail.editorScrollClientWidth = measurement.editorScrollClientWidth
    detail.editorScrollOffsetWidth = measurement.editorScrollOffsetWidth
    detail.contentClientWidth = measurement.contentClientWidth
    detail.contentInnerWidth = measurement.contentInnerWidth
    detail.borderInlineStartWidth = measurement.borderInlineStartWidth
    detail.borderInlineEndWidth = measurement.borderInlineEndWidth
    detail.paddingInlineStart = measurement.paddingInlineStart
    detail.paddingInlineEnd = measurement.paddingInlineEnd
    detail.scrollbarGutter = measurement.scrollbarGutter
    detail.activeTargetWidth = measurement.activeTargetWidth
    return detail
}

private fun EditorViewEffect.evidenceKind(): String = when (this) {
    is EditorViewEffect.Focus -> "focus"
    is EditorViewEffect.RestoreQueueScroll -> "restore-queue-scroll"
}

internal fun EditorFocusRef.evidenceName(): String = when (this) {
    is EditorFocusRef.Captured -> "captured:$token"
    is EditorFocusRef.ElementId -> "element:$value"
    is EditorFocusRef.QueueRow -> "queue-row:$keyId"
    is EditorFocusRef.Target -> "target:$keyId:${pluralCategory?.name ?: "singular"}"
    is EditorFocusRef.TargetSnapshot ->
        "target-snapshot:${target.keyId}:${target.pluralCategory?.name ?: "singular"}"
    EditorFocusRef.Search -> "search"
}
